import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import stateCityData from "../../../data/states.json";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaEdit,
  FaMapMarkerAlt,
  FaUsers,
  FaCheck,
  FaTimes,
  FaGlobe,
  FaBuilding,
  FaHashtag,
  FaUserCheck,
  FaSpinner,
  FaArrowLeft,
  FaSave,
  FaFilter,
  FaSearch,
  FaMicrophone,
  FaHeart,
  FaEye,
  FaUser,
  FaMoneyBillWave,
  FaVideo,
  FaCalendar,
  FaCalculator
} from "react-icons/fa";

const AdvertisementAdminModify = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    allStates: false,
    state: [],
    city: [],
    pincode: "",
    reportersIds: [],
    influencersIds: [],
  });


  const [cities, setCities] = useState([]);
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const states = Object.keys(stateCityData);
  const [reporters, setReporters] = useState([]); // Start with empty array
  const [influencers, setInfluencers] = useState([]);
  const location = useLocation();

  const [advertisementId, setAdvertisementId] = useState(null);
  const [advertisementData, setAdvertisementData] = useState(null);
  const [selectedReporters, setSelectedReporters] = useState([]);

  // Popup and loader states
  const [showPopup, setShowPopup] = useState(false);
  const [confirmModify, setConfirmModify] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showApproveSpinner, setShowApproveSpinner] = useState(false);

  useEffect(() => {
    console.log("🔍 Location state:", location.state);
    if (location.state) {
      // Check for both adId and advertisementId to handle different navigation sources
      const id = location.state.adId || location.state.advertisementId;
      if (id) {
        console.log("🔍 Setting advertisement ID:", id);
        setAdvertisementId(id);
      }
      if (location.state.advertisementData) {
        console.log("🔍 Setting advertisement data:", location.state.advertisementData);
        setAdvertisementData(location.state.advertisementData);
      }
    } else {
      console.log("❌ No location state provided");
    }
  }, [location]);

  // Fetch advertisement data if we have ID but no data
  useEffect(() => {
    const fetchAdvertisementData = async () => {
      if (advertisementId && !advertisementData) {
        try {
          console.log("🔍 Fetching advertisement data for ID:", advertisementId);
          const response = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/admin/get/advertisement-details/${advertisementId}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );

          if (response.data && response.data.success) {
            console.log("✅ Advertisement data fetched:", response.data.data);
            setAdvertisementData(response.data.data);
          } else {
            console.error("❌ Failed to fetch advertisement data");
            toast.error("Failed to fetch advertisement data");
          }
        } catch (error) {
          console.error("❌ Error fetching advertisement data:", error);
          toast.error("Error fetching advertisement data");
        }
      }
    };

    fetchAdvertisementData();
  }, [advertisementId, advertisementData]);

  // Fetch reporters from API
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // Fetch reporters
        const reportersResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/admin/get/all/reporter`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        setReporters(reportersResponse.data.reporters);

        // Fetch influencers
        const influencersResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/admin/get/all/reporter`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            userType: 'influencer'
          }
        });
        console.log("🔍 Influencers API response:", influencersResponse.data);
        setInfluencers(influencersResponse.data.reporters || []);
        console.log("🔍 Set influencers:", influencersResponse.data.reporters || []);
      } catch (error) {
        console.error("Failed to fetch users:", error);
        setReporters([]);
        setInfluencers([]);
      }
    };
    fetchUsers();
  }, []);

  // Update cities when state changes
  useEffect(() => {
    if (form.state.length > 0) {
      // Collect all cities from selected states
      let allCities = [];
      form.state.forEach((st) => {
        allCities = [...allCities, ...(stateCityData[st] || [])];
      });
      setCities([...new Set(allCities)]);
    } else {
      setCities([]);
    }
  }, [form.state]);

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    if (name === "allStates") {
      setForm((prev) => ({
        ...prev,
        allStates: checked,
        state: [],
        city: [],
        pincode: "",
        reportersIds: [], // Clear reporter selection when allStates is checked
        influencersIds: [], // Clear influencer selection when allStates is checked
      }));
    } else if (name === "state") {
      setForm((prev) => {
        const newStates = checked
          ? [...prev.state, value]
          : prev.state.filter((s) => s !== value);
        return {
          ...prev,
          state: newStates,
          city: prev.city.filter((c) =>
            newStates.some((st) => (stateCityData[st] || []).includes(c))
          ), // Remove cities not in selected states
        };
      });
    } else if (name === "city") {
      setForm((prev) => ({
        ...prev,
        city: checked
          ? [...prev.city, value]
          : prev.city.filter((c) => c !== value),
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation: If state is selected, city must be selected
    if (form.state.length > 0 && form.city.length === 0 && !form.allStates) {
      toast.error("Please select at least one city when you select a state.");
      return;
    }

    // Validation: Must select at least one reporter or influencer
    if (form.reportersIds.length === 0 && form.influencersIds.length === 0) {
      toast.error("Please select at least one reporter or influencer.");
      return;
    }

    // Validation: If pincode is entered, it should be 6 digits
    if (form.pincode && form.pincode.length !== 6) {
      toast.error("Pincode must be exactly 6 digits.");
      return;
    }

    setShowPopup(true);
  };

  // Filter reporters based on form selection, but always include selected reporters
  const isAnyFilterApplied =
    form.allStates ||
    form.state.length > 0 ||
    form.city.length > 0 ||
    form.pincode;

  let filteredReporters = [];
  let filteredInfluencers = [];

  console.log("🔍 Filtering influencers. Total influencers:", influencers.length);
  console.log("🔍 Form influencersIds:", form.influencersIds);
  console.log("🔍 Form allStates:", form.allStates);
  console.log("🔍 Form state:", form.state);
  console.log("🔍 Form city:", form.city);
  console.log("🔍 Form pincode:", form.pincode);

  if (!isAnyFilterApplied) {
    // Only show selected users if no filter is applied
    filteredReporters = reporters.filter((r) => form.reportersIds.includes(r._id));
    filteredInfluencers = influencers.filter((i) => form.influencersIds.includes(i._id));
    console.log("🔍 No filter applied. Filtered influencers:", filteredInfluencers.length);
  } else if (form.allStates) {
    filteredReporters = reporters;
    filteredInfluencers = influencers;
    console.log("🔍 All states selected. Filtered influencers:", filteredInfluencers.length);
  } else {
    // Filtered by state/city/pincode, but always include selected
    const filterUsers = (users) => {
      const filtered = users.filter((u) => {
        if (form.pincode) {
          return String(u.pincode) === String(form.pincode);
        } else if (form.city.length > 0) {
          return form.city.includes(u.city) && form.state.includes(u.state);
        } else if (form.state.length > 0) {
          return form.state.includes(u.state);
        }
        return false;
      });
      return filtered;
    };

    filteredReporters = filterUsers(reporters);
    filteredInfluencers = filterUsers(influencers);
    console.log("🔍 After filtering by location. Filtered influencers:", filteredInfluencers.length);

    // Always include selected users
    const selectedReporters = reporters.filter((r) => form.reportersIds.includes(r._id));
    const selectedInfluencers = influencers.filter((i) => form.influencersIds.includes(i._id));
    console.log("🔍 Selected influencers:", selectedInfluencers.length);

    // Merge and remove duplicates
    const mergedReporters = [...filteredReporters, ...selectedReporters];
    const mergedInfluencers = [...filteredInfluencers, ...selectedInfluencers];
    console.log("🔍 Merged influencers:", mergedInfluencers.length);

    filteredReporters = Array.from(new Map(mergedReporters.map((r) => [r._id, r])).values());
    filteredInfluencers = Array.from(new Map(mergedInfluencers.map((i) => [i._id, i])).values());
    console.log("🔍 Final filtered influencers:", filteredInfluencers.length);
  }

  // Add this handler
  const handleReporterSelect = (id) => {
    setForm((prev) => {
      const updated = prev.reportersIds.includes(id)
        ? prev.reportersIds.filter((rid) => rid !== id)
        : [...prev.reportersIds, id];
      return {
        ...prev,
        reportersIds: updated,
      }
    });
  };

  // Add influencer selection handler
  const handleInfluencerSelect = (id) => {
    setForm((prev) => {
      const updated = prev.influencersIds.includes(id)
        ? prev.influencersIds.filter((iid) => iid !== id)
        : [...prev.influencersIds, id];
      return {
        ...prev,
        influencersIds: updated,
      }
    });
  };

  // Add select all reporters handler
  const handleSelectAllReporters = () => {
    const availableReporterIds = filteredReporters
      .filter(reporter => {
        // Don't select reporters who are already sent
        const isAlreadySent = advertisementData?.acceptRejectReporterList?.some(
          existingReporter => existingReporter.reporterId === reporter._id
        );
        return !isAlreadySent;
      })
      .map(reporter => reporter._id);

    setForm((prev) => ({
      ...prev,
      reportersIds: [...new Set([...prev.reportersIds, ...availableReporterIds])]
    }));
  };

  // Add unselect all reporters handler
  const handleUnselectAllReporters = () => {
    setForm((prev) => ({
      ...prev,
      reportersIds: []
    }));
  };

  // Add select all influencers handler
  const handleSelectAllInfluencers = () => {
    const availableInfluencerIds = filteredInfluencers
      .filter(influencer => {
        // Don't select influencers who are already sent
        const isAlreadySent = advertisementData?.acceptRejectReporterList?.some(
          existingReporter => existingReporter.reporterId === influencer._id
        );
        return !isAlreadySent;
      })
      .map(influencer => influencer._id);

    setForm((prev) => ({
      ...prev,
      influencersIds: [...new Set([...prev.influencersIds, ...availableInfluencerIds])]
    }));
  };

  // Add unselect all influencers handler
  const handleUnselectAllInfluencers = () => {
    setForm((prev) => ({
      ...prev,
      influencersIds: []
    }));
  };

  // Add refs for dropdowns
  const stateDropdownRef = useRef(null);
  const cityDropdownRef = useRef(null);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        stateDropdownRef.current &&
        !stateDropdownRef.current.contains(event.target)
      ) {
        setShowStateDropdown(false);
      }
      if (
        cityDropdownRef.current &&
        !cityDropdownRef.current.contains(event.target)
      ) {
        setShowCityDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // API call function to modify advertisement
  const modifyAdvertisementAPI = async () => {
    setShowPopup(false); // First close the popup modal
    setTimeout(async () => {
      setShowApproveSpinner(true); // Then show spinner overlay (after popup closes)

      // Determine userType based on selected users
      let userType = "reporter"; // default
      if (form.reportersIds.length > 0 && form.influencersIds.length > 0) {
        userType = "both";
      } else if (form.influencersIds.length > 0) {
        userType = "influencer";
      } else if (form.reportersIds.length > 0) {
        userType = "reporter";
      }

      console.log(`🎯 Determined userType: ${userType} (reporters: ${form.reportersIds.length}, influencers: ${form.influencersIds.length})`);

      const payload = {
        action: "modified",
        note: "Advertisement modified with new targeting",
        selectedStates: form.state,
        adminSelectCities: form.city,
        adminSelectPincode: form.pincode,
        reporterId: [...form.reportersIds, ...form.influencersIds], // Combine both arrays
        allStates: form.allStates,
        userType: userType, // Add userType to payload
      };

      try {
        const response = await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/admin/modify/ads/${advertisementId}`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log("Advertisement modified successfully:", response);
        toast.success("Advertisement is successfully modified!");
        setTimeout(() => {
          setShowApproveSpinner(false);
          navigate("/admin/approvedposts");
        }, 1200);
      } catch (error) {
        setShowApproveSpinner(false);
        if (error.response && error.response.status === 500) {
          toast.error("Internal Server Error. Please try again later.");
        } else {
          toast.error("Failed to modify advertisement");
        }
      }
    }, 200); // 200ms delay to allow popup to close smoothly
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6">
      <ToastContainer position="top-right" autoClose={1200} />

      {/* Modern Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 rounded-2xl shadow-xl p-8 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                <FaMicrophone className="text-2xl" />
              </div>
              <div>
                <h1 className="text-3xl font-bold">Modify Advertisement</h1>
                <p className="text-blue-100 mt-2">Update advertisement targeting and user assignments</p>
                <p className="text-blue-200 mt-1">
                  Advertisement ID: {advertisementData?._id || advertisementId || "Loading..."}
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate("/admin/approvedposts")}
              className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-xl transition-colors backdrop-blur-sm"
            >
              <FaArrowLeft />
              <span>Back to Advertisements</span>
            </button>
          </div>
        </div>
      </div>

      {/* Spinner Overlay */}
      {showApproveSpinner && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-50">
          <div className="flex flex-col items-center p-8 bg-white rounded-2xl shadow-2xl border border-blue-200 animate-fadeIn">
            {/* Custom Tailwind Spinner */}
            <div className="relative mb-4 w-16 h-16 grid place-items-center">
              {/* Outer ring */}
              <span className="absolute inset-0 rounded-full border-4 border-transparent border-r-blue-500 animate-spin" style={{ borderWidth: "4px" }} />
              {/* Middle ring */}
              <span className="absolute inset-0 m-1 rounded-full border-4 border-transparent border-r-purple-500 animate-spin-slow" style={{ borderWidth: "4px", animationDuration: "2s" }} />
              {/* Inner ring */}
              <span className="absolute inset-0 m-2 rounded-full border-4 border-transparent border-r-indigo-500 animate-spin-slower" style={{ borderWidth: "4px", animationDuration: "3s" }} />
            </div>
            <span className="text-lg font-semibold text-blue-700 tracking-wide">
              Processing Modification...
            </span>
            <span className="text-sm text-gray-500 mt-2">
              Please wait while we update the advertisement.
            </span>
          </div>
        </div>
      )}

      {/* Modern Popup Modal */}
      {showPopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-50">
          <div className="bg-white p-8 rounded-2xl shadow-2xl w-96 border border-gray-100">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FaEdit className="text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Confirm Modification</h3>
            </div>
            <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="confirmModify"
                  checked={confirmModify}
                  onChange={e => setConfirmModify(e.target.checked)}
                  className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                />
                <label htmlFor="confirmModify" className="text-gray-700 font-medium">
                  I confirm that I want to modify this advertisement
                </label>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                className="flex items-center space-x-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors"
                onClick={() => { setShowPopup(false); setConfirmModify(false); }}
                disabled={showApproveSpinner}
              >
                <FaTimes />
                <span>Cancel</span>
              </button>
              <button
                className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-medium transition-colors ${(!confirmModify || showApproveSpinner)
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white shadow-lg'
                  }`}
                disabled={!confirmModify || showApproveSpinner}
                onClick={() => modifyAdvertisementAPI()}
              >
                <FaSave />
                <span>Modify Advertisement</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Advertisement Overview Section */}
      {advertisementData && (
        <div className="max-w-7xl mx-auto mb-8">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-100 p-6 rounded-2xl border border-blue-200">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-blue-500 text-white rounded-lg p-2">
                <FaEye className="w-4 h-4" />
              </div>
              <div>
                <h5 className="font-semibold text-gray-800">Advertisement Being Modified</h5>
                <p className="text-sm text-gray-600">Complete details of the advertisement you are about to modify</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Advertisement ID */}
              <div className="bg-white rounded-xl p-4 border border-blue-200 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <FaUser className="w-4 h-4 text-blue-600" />
                  <span className="text-xs font-semibold text-gray-500 uppercase">Ad ID</span>
                </div>
                <div className="text-sm font-mono text-blue-600 break-all">{advertisementData._id}</div>
              </div>

              {/* Ad Type */}
              <div className="bg-white rounded-xl p-4 border border-blue-200 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <FaFilter className="w-4 h-4 text-blue-600" />
                  <span className="text-xs font-semibold text-gray-500 uppercase">Ad Type</span>
                </div>
                <div className="text-lg font-bold text-blue-600">{advertisementData.adType}</div>
              </div>

              {/* Target User Type */}
              <div className="bg-white rounded-xl p-4 border border-blue-200 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <FaUser className="w-4 h-4 text-blue-600" />
                  <span className="text-xs font-semibold text-gray-500 uppercase">Target</span>
                </div>
                <div className="text-lg font-bold text-blue-600">
                  {advertisementData.userType === 'influencer' ? '🎯 Influencer' : '📰 Reporter'}
                </div>
              </div>

              {/* Media Type */}
              <div className="bg-white rounded-xl p-4 border border-blue-200 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <FaVideo className="w-4 h-4 text-blue-600" />
                  <span className="text-xs font-semibold text-gray-500 uppercase">Media</span>
                </div>
                <div className="text-lg font-bold text-blue-600 capitalize">{advertisementData.mediaType}</div>
              </div>

              {/* Total Budget */}
              <div className="bg-white rounded-xl p-4 border border-green-200 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <FaMoneyBillWave className="w-4 h-4 text-green-600" />
                  <span className="text-xs font-semibold text-gray-500 uppercase">Total Budget</span>
                </div>
                <div className="text-xl font-bold text-green-600">₹{advertisementData.totalCost}</div>
              </div>

              {/* Required Views */}
              <div className="bg-white rounded-xl p-4 border border-purple-200 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <FaEye className="w-4 h-4 text-purple-600" />
                  <span className="text-xs font-semibold text-gray-500 uppercase">Required Views</span>
                </div>
                <div className="text-xl font-bold text-purple-600">{advertisementData.requiredViews?.toLocaleString() || "N/A"}</div>
              </div>

              {/* Current Base View */}
              <div className="bg-white rounded-xl p-4 border border-orange-200 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <FaCalculator className="w-4 h-4 text-orange-600" />
                  <span className="text-xs font-semibold text-gray-500 uppercase">Current Base View</span>
                </div>
                <div className="text-xl font-bold text-orange-600">{advertisementData.baseView || "N/A"}</div>
              </div>

              {/* Current Required Reporters */}
              <div className="bg-white rounded-xl p-4 border border-indigo-200 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <FaUser className="w-4 h-4 text-indigo-600" />
                  <span className="text-xs font-semibold text-gray-500 uppercase">Current Reporters</span>
                </div>
                <div className="text-xl font-bold text-indigo-600">{advertisementData.requiredReporter || "N/A"}</div>
              </div>
            </div>

            {/* Campaign Timeline */}
            <div className="mt-6 bg-white rounded-xl p-4 border border-blue-200">
              <h6 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                <FaCalendar className="w-4 h-4" />
                Campaign Timeline
              </h6>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="text-center">
                  <div className="text-lg font-bold text-blue-700">
                    {new Date(advertisementData.startDate).toLocaleDateString('en-IN', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </div>
                  <div className="text-xs text-blue-600">Start Date</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-green-700">
                    {new Date(advertisementData.endDate).toLocaleDateString('en-IN', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </div>
                  <div className="text-xs text-green-600">End Date</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-purple-700">
                    {advertisementData.adLength}
                  </div>
                  <div className="text-xs text-purple-600">Ad Length</div>
                </div>
              </div>
            </div>

            {/* Preference Targeting */}
            {(advertisementData.pfState || (advertisementData.pfCities && advertisementData.pfCities.length > 0)) && (
              <div className="mt-6 bg-white rounded-xl p-4 border border-blue-200">
                <h6 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                  <FaMapMarkerAlt className="w-4 h-4" />
                  Current Preference Targeting
                </h6>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="text-center">
                    <div className="text-lg font-bold text-blue-700">
                      {advertisementData.pfState || "Not specified"}
                    </div>
                    <div className="text-xs text-blue-600">Preference State</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-700">
                      {advertisementData.pfCities && advertisementData.pfCities.length > 0 ? advertisementData.pfCities.join(", ") : "Not specified"}
                    </div>
                    <div className="text-xs text-green-600">Preference Cities</div>
                  </div>
                </div>
              </div>
            )}

            {/* Current Reporter/Influencer Assignments */}
            {advertisementData.acceptRejectReporterList && advertisementData.acceptRejectReporterList.length > 0 && (
              <div className="mt-6 bg-white rounded-xl p-4 border border-blue-200">
                <h6 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
                  <FaUsers className="w-4 h-4" />
                  Current Assignments
                </h6>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="text-center">
                    <div className="text-lg font-bold text-blue-700">
                      {advertisementData.acceptRejectReporterList.length}
                    </div>
                    <div className="text-xs text-blue-600">Total Assigned</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-700">
                      {advertisementData.acceptRejectReporterList.filter(r => r.postStatus === 'accepted').length}
                    </div>
                    <div className="text-xs text-green-600">Accepted</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Form Section */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-6">
              <h2 className="text-2xl font-bold text-white flex items-center space-x-3">
                <FaFilter />
                <span>Targeting Configuration</span>
              </h2>
              <p className="text-blue-100 mt-2">Configure advertisement targeting parameters</p>
            </div>

            <form onSubmit={handleSubmit} className="p-8 space-y-8">
              {/* All States Selection */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200">
                <div className="flex items-center space-x-4">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <FaGlobe className="text-green-600" />
                  </div>
                  <div className="flex-1">
                    <label htmlFor="allStates" className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        id="allStates"
                        name="allStates"
                        checked={form.allStates}
                        onChange={handleChange}
                        className="w-5 h-5 text-green-600 rounded focus:ring-green-500"
                      />
                      <span className="text-lg font-semibold text-gray-800">Select All States</span>
                    </label>
                    <p className="text-gray-600 mt-1">Target all states and territories</p>
                  </div>
                </div>
              </div>

              {/* State Selection */}
              <div className="space-y-4" ref={stateDropdownRef}>
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FaMapMarkerAlt className="text-blue-600" />
                  </div>
                  <label className="text-lg font-semibold text-gray-800">Select States</label>
                </div>
                <div className="relative">
                  <button
                    type="button"
                    className="w-full p-4 border-2 border-gray-200 rounded-xl text-left bg-white hover:border-blue-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => setShowStateDropdown((prev) => !prev)}
                    disabled={form.allStates}
                  >
                    <div className="flex items-center justify-between">
                      <span className={form.state.length > 0 ? "text-gray-800" : "text-gray-500"}>
                        {form.state.length > 0 ? `${form.state.length} state(s) selected` : "Select State(s)"}
                      </span>
                      <FaSearch className="text-gray-400" />
                    </div>
                  </button>
                  {showStateDropdown && !form.allStates && (
                    <div className="absolute z-10 bg-white border-2 border-gray-200 rounded-xl shadow-xl w-full max-h-60 overflow-y-auto mt-2">
                      {states.map((state) => (
                        <label
                          key={state}
                          className="flex items-center px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          <input
                            type="checkbox"
                            name="state"
                            value={state}
                            checked={form.state.includes(state)}
                            onChange={handleChange}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 mr-3"
                          />
                          <span className="text-gray-700">{state}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                {/* Show selected states */}
                {form.state.length > 0 && (
                  <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                    <div className="flex items-center space-x-2 mb-2">
                      <FaCheck className="text-blue-600" />
                      <span className="font-medium text-blue-800">Selected States:</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {form.state.map((state) => (
                        <span key={state} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                          {state}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {/* City Selection */}
              <div className="space-y-4" ref={cityDropdownRef}>
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <FaBuilding className="text-purple-600" />
                  </div>
                  <label className="text-lg font-semibold text-gray-800">Select Cities</label>
                </div>
                <div className="relative">
                  <button
                    type="button"
                    className={`w-full p-4 border-2 rounded-xl text-left bg-white hover:border-purple-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${form.state.length > 0 && form.city.length === 0 && !form.allStates
                        ? "border-red-300 bg-red-50"
                        : "border-gray-200"
                      }`}
                    onClick={() => setShowCityDropdown((prev) => !prev)}
                    disabled={form.allStates || form.state.length === 0}
                  >
                    <div className="flex items-center justify-between">
                      <span className={form.city.length > 0 ? "text-gray-800" : "text-gray-500"}>
                        {form.city.length > 0 ? `${form.city.length} city(ies) selected` : "Select City(ies)"}
                      </span>
                      <FaSearch className="text-gray-400" />
                    </div>
                  </button>
                  {showCityDropdown && !form.allStates && form.state.length > 0 && (
                    <div className="absolute z-10 bg-white border-2 border-gray-200 rounded-xl shadow-xl w-full max-h-60 overflow-y-auto mt-2">
                      {cities.map((city) => (
                        <label
                          key={city}
                          className="flex items-center px-4 py-3 hover:bg-purple-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          <input
                            type="checkbox"
                            name="city"
                            value={city}
                            checked={form.city.includes(city)}
                            onChange={handleChange}
                            className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500 mr-3"
                          />
                          <span className="text-gray-700">{city}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* Warning message when state is selected but no city */}
                {form.state.length > 0 && form.city.length === 0 && !form.allStates && (
                  <div className="p-3 bg-red-50 rounded-xl border border-red-200">
                    <div className="flex items-center space-x-2">
                      <FaTimes className="text-red-600" />
                      <span className="text-sm font-medium text-red-800">
                        Please select at least one city for the selected state(s)
                      </span>
                    </div>
                  </div>
                )}

                {/* Show selected cities */}
                {form.city.length > 0 && (
                  <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
                    <div className="flex items-center space-x-2 mb-2">
                      <FaCheck className="text-purple-600" />
                      <span className="font-medium text-purple-800">Selected Cities:</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {form.city.map((city) => (
                        <span key={city} className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                          {city}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Pincode Selection */}
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <FaHashtag className="text-indigo-600" />
                  </div>
                  <label htmlFor="pincode" className="text-lg font-semibold text-gray-800">
                    Pincode
                  </label>
                </div>
                <div className="relative">
                  <input
                    type="number"
                    id="pincode"
                    name="pincode"
                    value={form.pincode}
                    onChange={handleChange}
                    placeholder="Enter 6-digit pincode"
                    className="w-full p-4 border-2 border-gray-200 rounded-xl bg-white hover:border-indigo-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={
                      form.allStates || form.state.length > 1 || form.city.length > 1 || form.influencersIds.length > 0
                    }
                    maxLength={6}
                  />
                  <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                    <FaHashtag className="text-gray-400" />
                  </div>
                </div>
                <p className="text-sm text-gray-600">
                  Enter a specific 6-digit pincode for precise targeting
                </p>
              </div>

              {/* Validation Warning */}
              {(form.reportersIds.length === 0 && form.influencersIds.length === 0) && (
                <div className="p-4 bg-red-50 rounded-xl border border-red-200">
                  <div className="flex items-center space-x-2">
                    <FaTimes className="text-red-600" />
                    <span className="text-sm font-medium text-red-800">
                      Please select at least one reporter or influencer to proceed
                    </span>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="pt-6">
                <button
                  type="submit"
                  className={`w-full py-4 px-6 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center space-x-3 ${(form.reportersIds.length === 0 && form.influencersIds.length === 0) ||
                      (form.state.length > 0 && form.city.length === 0 && !form.allStates)
                      ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                      : "bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white"
                    }`}
                  disabled={
                    (form.reportersIds.length === 0 && form.influencersIds.length === 0) ||
                    (form.state.length > 0 && form.city.length === 0 && !form.allStates)
                  }
                >
                  <FaSave />
                  <span>Modify Advertisement</span>
                </button>
              </div>
            </form>
          </div>
        </div>
        {/* User Selection Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden sticky top-6">
            <div className="bg-gradient-to-r from-green-500 to-teal-600 p-6">
              <h2 className="text-xl font-bold text-white flex items-center space-x-3">
                <FaUsers />
                <span>User Selection</span>
              </h2>
              <p className="text-green-100 mt-2">Choose specific users for this advertisement</p>
            </div>

            <div className="p-6">
              {/* Summary Section */}
              <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                <div className="flex items-center space-x-2 mb-3">
                  <FaUsers className="text-blue-600" />
                  <span className="font-medium text-blue-800">User Summary</span>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-blue-700 font-medium">Reporters:</span>
                    <div className="mt-1">
                      <span className="text-green-600">
                        {filteredReporters.filter(r =>
                          advertisementData?.acceptRejectReporterList?.some(
                            existing => existing.reporterId === r._id
                          )
                        ).length} already sent
                      </span>
                      <span className="text-gray-600 ml-2">
                        {filteredReporters.filter(r =>
                          !advertisementData?.acceptRejectReporterList?.some(
                            existing => existing.reporterId === r._id
                          )
                        ).length} available
                      </span>
                    </div>
                  </div>
                  <div>
                    <span className="text-blue-700 font-medium">Influencers:</span>
                    <div className="mt-1">
                      <span className="text-green-600">
                        {filteredInfluencers.filter(i =>
                          advertisementData?.acceptRejectReporterList?.some(
                            existing => existing.reporterId === i._id
                          )
                        ).length} already sent
                      </span>
                      <span className="text-gray-600 ml-2">
                        {filteredInfluencers.filter(i =>
                          !advertisementData?.acceptRejectReporterList?.some(
                            existing => existing.reporterId === i._id
                          )
                        ).length} available
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Reporters Section */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-600 flex items-center">
                    <FaUserCheck className="mr-1 text-blue-500" />
                    Available Reporters
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                      {filteredReporters.length} found
                    </span>
                    {filteredReporters.length > 0 && (
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={handleSelectAllReporters}
                          className="text-xs bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded transition-colors"
                          title="Select all reporters"
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          onClick={handleUnselectAllReporters}
                          className="text-xs bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded transition-colors"
                          title="Unselect all reporters"
                        >
                          Unselect All
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {filteredReporters.length > 0 ? (
                  <div className="space-y-4">
                    {/* Available Reporters Section */}
                    {filteredReporters.filter(reporter =>
                      !advertisementData?.acceptRejectReporterList?.some(
                        existing => existing.reporterId === reporter._id
                      )
                    ).length > 0 && (
                        <div>
                          <div className="flex items-center space-x-2 mb-3">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            <span className="text-sm font-medium text-blue-700">Available Reporters</span>
                          </div>
                          <div className="space-y-2 max-h-32 overflow-y-auto">
                            {filteredReporters
                              .filter(reporter =>
                                !advertisementData?.acceptRejectReporterList?.some(
                                  existing => existing.reporterId === reporter._id
                                )
                              )
                              .map((reporter, idx) => (
                                <div key={idx} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                                  <input
                                    name="reportersIds"
                                    value={reporter._id}
                                    type="checkbox"
                                    checked={form.reportersIds.includes(reporter._id)}
                                    onChange={() => handleReporterSelect(reporter._id)}
                                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                                  />
                                  <div className="flex-1">
                                    <div className="flex items-center space-x-2">
                                      <FaUserCheck className="text-gray-400 text-sm" />
                                      <span className="font-medium text-gray-800">{reporter.name}</span>
                                    </div>
                                    {reporter.iinsafId && (
                                      <p className="text-xs text-gray-500 mt-1">ID: {reporter.iinsafId}</p>
                                    )}
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}

                    {/* Already Sent Reporters Section */}
                    {filteredReporters.filter(reporter =>
                      advertisementData?.acceptRejectReporterList?.some(
                        existing => existing.reporterId === reporter._id
                      )
                    ).length > 0 && (
                        <div>
                          <div className="flex items-center space-x-2 mb-3">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <span className="text-sm font-medium text-green-700">Already Sent Reporters</span>
                          </div>
                          <div className="space-y-2 max-h-32 overflow-y-auto">
                            {filteredReporters
                              .filter(reporter =>
                                advertisementData?.acceptRejectReporterList?.some(
                                  existing => existing.reporterId === reporter._id
                                )
                              )
                              .map((reporter, idx) => (
                                <div key={idx} className="flex items-center space-x-3 p-3 bg-green-50 rounded-xl border border-green-200">
                                  <input
                                    name="reportersIds"
                                    value={reporter._id}
                                    type="checkbox"
                                    checked={false}
                                    disabled={true}
                                    className="w-4 h-4 text-green-600 rounded focus:ring-green-500 opacity-50"
                                  />
                                  <div className="flex-1">
                                    <div className="flex items-center space-x-2">
                                      <FaUserCheck className="text-green-500 text-sm" />
                                      <span className="font-medium text-green-800">{reporter.name}</span>
                                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">
                                        Already Sent
                                      </span>
                                    </div>
                                    {reporter.iinsafId && (
                                      <p className="text-xs text-green-600 mt-1">ID: {reporter.iinsafId}</p>
                                    )}
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <div className="p-2 bg-gray-100 rounded-full w-12 h-12 mx-auto mb-2 flex items-center justify-center">
                      <FaUsers className="text-gray-400 text-lg" />
                    </div>
                    <p className="text-gray-500 text-sm">No reporters found</p>
                  </div>
                )}
              </div>

              {/* Influencers Section */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-600 flex items-center">
                    <FaHeart className="mr-1 text-pink-500" />
                    Available Influencers
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs bg-pink-100 text-pink-800 px-2 py-1 rounded-full">
                      {filteredInfluencers.length} found
                    </span>
                    {filteredInfluencers.length > 0 && (
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={handleSelectAllInfluencers}
                          className="text-xs bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded transition-colors"
                          title="Select all influencers"
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          onClick={handleUnselectAllInfluencers}
                          className="text-xs bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded transition-colors"
                          title="Unselect all influencers"
                        >
                          Unselect All
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {filteredInfluencers.length > 0 ? (
                  <div className="space-y-4">
                    {/* Available Influencers Section */}
                    {filteredInfluencers.filter(influencer =>
                      !advertisementData?.acceptRejectReporterList?.some(
                        existing => existing.reporterId === influencer._id
                      )
                    ).length > 0 && (
                        <div>
                          <div className="flex items-center space-x-2 mb-3">
                            <div className="w-2 h-2 bg-pink-500 rounded-full"></div>
                            <span className="text-sm font-medium text-pink-700">Available Influencers</span>
                          </div>
                          <div className="space-y-2 max-h-32 overflow-y-auto">
                            {filteredInfluencers
                              .filter(influencer =>
                                !advertisementData?.acceptRejectReporterList?.some(
                                  existing => existing.reporterId === influencer._id
                                )
                              )
                              .map((influencer, idx) => (
                                <div key={idx} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                                  <input
                                    name="influencersIds"
                                    value={influencer._id}
                                    type="checkbox"
                                    checked={form.influencersIds.includes(influencer._id)}
                                    onChange={() => handleInfluencerSelect(influencer._id)}
                                    className="w-4 h-4 text-pink-600 rounded focus:ring-pink-500"
                                  />
                                  <div className="flex-1">
                                    <div className="flex items-center space-x-2">
                                      <FaHeart className="text-gray-400 text-sm" />
                                      <span className="font-medium text-gray-800">{influencer.name}</span>
                                    </div>
                                    {influencer.iinsafId && (
                                      <p className="text-xs text-gray-500 mt-1">ID: {influencer.iinsafId}</p>
                                    )}
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}

                    {/* Already Sent Influencers Section */}
                    {filteredInfluencers.filter(influencer =>
                      advertisementData?.acceptRejectReporterList?.some(
                        existing => existing.reporterId === influencer._id
                      )
                    ).length > 0 && (
                        <div>
                          <div className="flex items-center space-x-2 mb-3">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <span className="text-sm font-medium text-green-700">Already Sent Influencers</span>
                          </div>
                          <div className="space-y-2 max-h-32 overflow-y-auto">
                            {filteredInfluencers
                              .filter(influencer =>
                                advertisementData?.acceptRejectReporterList?.some(
                                  existing => existing.reporterId === influencer._id
                                )
                              )
                              .map((influencer, idx) => (
                                <div key={idx} className="flex items-center space-x-3 p-3 bg-green-50 rounded-xl border border-green-200">
                                  <input
                                    name="influencersIds"
                                    value={influencer._id}
                                    type="checkbox"
                                    checked={false}
                                    disabled={true}
                                    className="w-4 h-4 text-green-600 rounded focus:ring-green-500 opacity-50"
                                  />
                                  <div className="flex-1">
                                    <div className="flex items-center space-x-2">
                                      <FaHeart className="text-green-500 text-sm" />
                                      <span className="font-medium text-green-800">{influencer.name}</span>
                                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">
                                        Already Sent
                                      </span>
                                    </div>
                                    {influencer.iinsafId && (
                                      <p className="text-xs text-green-600 mt-1">ID: {influencer.iinsafId}</p>
                                    )}
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <div className="p-2 bg-gray-100 rounded-full w-12 h-12 mx-auto mb-2 flex items-center justify-center">
                      <FaHeart className="text-gray-400 text-lg" />
                    </div>
                    <p className="text-gray-500 text-sm">No influencers found</p>
                  </div>
                )}
              </div>

              {/* Selected Users Summary */}
              {(form.reportersIds.length > 0 || form.influencersIds.length > 0) && (
                <div className="mt-6 p-4 bg-green-50 rounded-xl border border-green-200">
                  <div className="flex items-center space-x-2 mb-3">
                    <FaCheck className="text-green-600" />
                    <span className="font-medium text-green-800">Selected Users</span>
                  </div>
                  <div className="space-y-2">
                    {/* Selected Reporters */}
                    {reporters
                      .filter(r => form.reportersIds.includes(r._id))
                      .map((reporter) => (
                        <div key={reporter._id} className="flex items-center justify-between p-2 bg-white rounded-lg">
                          <div className="flex items-center space-x-2">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">Reporter</span>
                            <span className="text-sm font-medium text-gray-700">{reporter.name}</span>
                          </div>
                          <button
                            onClick={() => handleReporterSelect(reporter._id)}
                            className="text-red-500 hover:text-red-700 text-sm"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ))}
                    {/* Selected Influencers */}
                    {influencers
                      .filter(i => form.influencersIds.includes(i._id))
                      .map((influencer) => (
                        <div key={influencer._id} className="flex items-center justify-between p-2 bg-white rounded-lg">
                          <div className="flex items-center space-x-2">
                            <span className="px-2 py-1 bg-pink-100 text-pink-800 rounded-full text-xs font-medium">Influencer</span>
                            <span className="text-sm font-medium text-gray-700">{influencer.name}</span>
                          </div>
                          <button
                            onClick={() => handleInfluencerSelect(influencer._id)}
                            className="text-red-500 hover:text-red-700 text-sm"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdvertisementAdminModify;